import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { IHistory } from '../../interfaces';
import { UserModel } from '../../model/UserModel';
import { getUserDetails, getOrgMFAStatus, toggleOrgMFA } from '../../services/userService';
import { Async } from '../reusableComponents/Async';
import { BasePage } from '../reusableComponents/BasePage';
import { ErrorPage } from '../reusableComponents/ErrorPage';
import { Icon } from '../reusableComponents/Icon';
import { Loader } from '../reusableComponents/Loader/Loader';
import { getCurrentUserUUID } from '../../utils/generalUtils';
import { showAlert } from '../../actions/miscellaneousActions';

export interface IUserProfilePageProps {
    id: string;
    instance: UserModel;
    history: IHistory;
    userSignBase64: string;
    userImageBase64: string;
}

export interface IUserProfilePageState {
    orgMFAEnforced: boolean;
    isOrgToggling: boolean;
    isLoading: boolean;
}

export class UserProfilePageImpl extends React.Component<IUserProfilePageProps, IUserProfilePageState> {
    constructor(props: IUserProfilePageProps) {
        super(props);
        this.state = {
            orgMFAEnforced: false,
            isOrgToggling: false,
            isLoading: false
        };
    }

    public componentDidMount() {
        this.fetchOrgMFAStatus();
    }

    public componentDidUpdate(prevProps: IUserProfilePageProps) {
        if (!prevProps.instance && this.props.instance) {
            this.fetchOrgMFAStatus();
        }
    }

    public fetchOrgMFAStatus = async () => {
        this.setState({ isLoading: true });
        try {
            const response = await getOrgMFAStatus();
            if (response && response.success) {
                this.setState({
                    orgMFAEnforced: response.mfa_enforced === true,
                    isLoading: false
                });
            }
            else {
            this.setState({ isLoading: false });  
        }
        } catch (error) {
            console.error('Fetch Org MFA error:', error);
            this.setState({ isLoading: false });
        }
    }

    public toggleOrgMFA = async () => {
        const { orgMFAEnforced } = this.state;
        const newStatus = !orgMFAEnforced;
        
        this.setState({ isOrgToggling: true });
        
        try {
            const response = await toggleOrgMFA(newStatus);
            
            if (response && response.success) {
                this.setState({
                    orgMFAEnforced: response.mfa_enforced === true,
                    isOrgToggling: false
                });
                
                showAlert({
                    title: 'Success',
                    text: `Organization MFA ${newStatus ? 'enabled' : 'disabled'} successfully`,
                    type: 'success',
                    closeOnConfirm: true
                }, 'submission');
            } else {
                throw new Error(response?.message || 'Failed to toggle organization MFA');
            }
        } catch (error) {
            console.error('Toggle Org MFA error:', error);
            this.setState({ isOrgToggling: false });
            
            showAlert({
                title: 'Error',
                text: (error as any)?.message ,
                type: 'error',
                closeOnConfirm: true
            }, 'submission');
        }
    };

    public promise = async () => {
        if (this.props.id !== getCurrentUserUUID()) {
            await getUserDetails(this.props.id);
        }
    }

    public renderDetails = () => {
        if (this.state.isLoading) {
        return <Loader type="async" />;
    }
        if (!this.props.instance) {
            return <Loader type="async" />;
        }
        
        
        const { FirstName, Email, AccessLevel, JobTitle,
            MiddleName, LastName, OrganizationName, PhoneNumber, JobDescription } = this.props.instance.props;
        const { orgMFAEnforced, isOrgToggling} = this.state;
        const userLevel = AccessLevel;
        const isL2User = userLevel === 'L2';

        return <div className="details-page">
            <div className="container" >
                <DetailsRow label="Name:" value={`${FirstName} ${MiddleName} ${LastName}`} />
                <DetailsRow label="Email:" value={Email} />
                <DetailsRow label="Access Level:" value={AccessLevel} />
                <DetailsRow label="Organization:" value={OrganizationName} />
                <DetailsRow label="Phone Number:" value={PhoneNumber} />
                <DetailsRow label="Job Title:" value={JobTitle} />
                <DetailsRow label="Job Description:" value={JobDescription} />
                
                <div className="details" >
                    <div className="label" >Signature:</div>
                    <div className="value" ><img className="signature-box" src={
                        this.props.userSignBase64 || this.props.instance && this.props.instance.props.SignURL + '?expires=' + Date.now()
                    } /></div>
                </div>

                {isL2User && (
                    <div className="details org-mfa-toggle-row">
                        <div className="label" style={{ fontWeight: '500' }}>
                            Organization MFA:
                        </div>
                        <div className="values" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{
                                fontSize: '13px',
                                color: orgMFAEnforced ? '#05c46b' : '#dc3545',
                                fontWeight: '500'
                            }}>
                                {orgMFAEnforced ? 'ON' : 'OFF'}
                            </span>
                            <label className="toggle-switch">
                                <input 
                                    type="checkbox" 
                                    checked={orgMFAEnforced === true}
                                    onChange={this.toggleOrgMFA}
                                    disabled={isOrgToggling}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>
                )}
            </div>
        </div>;
    }

    public render() {
        return <BasePage
            pic={this.props.userImageBase64 || this.props.instance && this.props.instance.props.UserImage + '?expires=' + Date.now()}
            title={this.props.instance ?
                `${(this.props.instance && this.props.instance.props.FirstName) + `'s`} Profile`
                : 'User Profile'}>
            <Async
                identifier="User Details Page"
                promise={this.promise}
                loader={<Loader type="async" />}
                content={this.renderDetails()}
                error={<ErrorPage />}
            />
            <div className="options-container" >
                <div className="add-new" 
                onClick={() => this.props.history.push(`/users/edit/${this.props.instance.props.id}`)}>
                    <Icon icon="pencil-alt" />
                    &nbsp;
                    Edit Profile
                </div>
            </div>
        </BasePage>;
    }
}

export function mapStateToProps(state, ownProps) {
    const instance = UserModel.get(ownProps.match?.params?.id);
    return {
        id: ownProps.match.params.id,
        instance: instance,
        userImageBase64: state.miscellaneous.get('userImageBase64'),
        userSignBase64: state.miscellaneous.get('userSignBase64')
    };
}

export const UserProfilePage = 
withRouter(connect<{}, {}, IUserProfilePageProps>(mapStateToProps)(UserProfilePageImpl) as any);

const DetailsRow = ({ label, value }) => {
    return <div className="details" >
        <div className="label" >{label}</div>
        <div className="value" >{value || 'NA'}</div>
    </div>;
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Profile/UserProfilePage.tsx